"use client";

import { TextLink } from "@/components/links/TextLink";
import { Badge } from "@/components/text/Badge";
import { SoftText } from "@/components/text/SoftText";
import { FilePresentation } from "@/components/uploadFilePresentation/FilePresentation";
import {
  BAD_AUDIO_CONVERSION_DESCRIPTION,
  BAD_AUDIO_MODEL_DESCRIPTION,
} from "@/constants/audioQuality";
import { AudioQualityCheck } from "@/types/defs/audioQuality/audioQualityCheck";
import { AudioInputState, SelectedFile } from "@/types/types";
import { getAudioIsolationRecommendations } from "@/utils/audio/quality/getAudioIsolationRecommendations";
import { getModelEffectsDetection } from "@/utils/audio/quality/getModelEffectsDetection";
import { isAudioQualityCheckPending } from "@/utils/selectedFileCover/is/isAudioQualityCheckPending";

// The two surfaces derive the same two badge flags through different rules: conversion reuses the isolation recommendation tiers, model training flags each effect independently against the recommended thresholds.
const getEffectsDetection = (
  surface: "conversion" | "model",
  audioQualityCheck: AudioQualityCheck | null
): { hasInstrumentalDetected: boolean; hasReverbDetected: boolean } => {
  if (surface === "model") {
    return getModelEffectsDetection(audioQualityCheck);
  }
  const { instrumental, reverb } = getAudioIsolationRecommendations([
    audioQualityCheck,
  ]);
  return {
    hasInstrumentalDetected: instrumental !== "off",
    hasReverbDetected: reverb !== "off",
  };
};

// The auto-settings badge and the instrument gate only exist on the conversion surface, so the surface discriminant carries them — a model call site cannot express them at all.
export function AudioQualityFilePresentation({
  selectedFile,
  clearInput,
  hideClearButton,
  shouldShowAudioQualityDescription,
  ...surfaceProps
}: {
  selectedFile: SelectedFile;
  clearInput: AudioInputState["clearInput"];
  hideClearButton: boolean;
  shouldShowAudioQualityDescription: boolean;
} & (
  | {
      surface: "conversion";
      showAutoSettingsBadge: boolean;
      isInstrumentSelected: boolean;
    }
  | { surface: "model" }
)) {
  const { hasInstrumentalDetected, hasReverbDetected } = getEffectsDetection(
    surfaceProps.surface,
    selectedFile.audioQualityCheck
  );
  const shouldShowQualityIssuesBadges =
    (surfaceProps.surface === "model" || !surfaceProps.isInstrumentSelected) &&
    (hasInstrumentalDetected || hasReverbDetected);

  const shouldShowAnalyzingState = isAudioQualityCheckPending(selectedFile);

  return (
    <FilePresentation
      selectedFile={selectedFile}
      clearInput={clearInput}
      hideClearButton={hideClearButton}
      footerSlot={
        <>
          {shouldShowAnalyzingState && (
            <div className="mt-3 flex items-center gap-2.5 border-t border-gray-200 pt-3">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              <SoftText
                elementType="p"
                className="text-xs font-normal text-gray-700"
              >
                Analyzing audio
              </SoftText>
            </div>
          )}

          {shouldShowQualityIssuesBadges && (
            <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
              <div className="flex flex-wrap gap-2">
                {hasInstrumentalDetected && (
                  <Badge
                    dotColor="red"
                    className="bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
                  >
                    Instrumental detected
                  </Badge>
                )}
                {hasReverbDetected && (
                  <Badge
                    dotColor="red"
                    className="bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
                  >
                    Reverb/Echo detected
                  </Badge>
                )}
                {surfaceProps.surface === "conversion" &&
                  surfaceProps.showAutoSettingsBadge && (
                    <Badge
                      dotColor="green"
                      className="bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
                    >
                      Auto settings applied
                    </Badge>
                  )}
              </div>
              {shouldShowAudioQualityDescription &&
                (surfaceProps.surface === "model" ?
                  <>
                    <SoftText
                      elementType="p"
                      className="text-xs font-normal text-gray-500"
                    >
                      {BAD_AUDIO_MODEL_DESCRIPTION}
                    </SoftText>
                    <SoftText
                      elementType="p"
                      className="text-xs font-normal text-gray-500"
                    >
                      If you are training a vocal clone, you can isolate your
                      recording{" "}
                      <TextLink
                        href="/isolate"
                        external={false}
                        className="text-xs"
                      >
                        here
                      </TextLink>
                      .
                    </SoftText>
                  </>
                : <SoftText
                    elementType="p"
                    className="text-xs font-normal text-gray-500"
                  >
                    {BAD_AUDIO_CONVERSION_DESCRIPTION}
                  </SoftText>)}
            </div>
          )}
        </>
      }
    />
  );
}
